import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppProvider as PolarisProvider } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import '@shopify/polaris/build/esm/styles.css';

import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initAppBridge } from './config/shopify-app-bridge';

// Shopify always passes `host` query param when embedding.
// If missing, we're loaded outside Shopify admin (e.g. local dev preview) — render but skip App Bridge.
const params = new URLSearchParams(window.location.search);
const host = params.get('host');
const shop = params.get('shop');

if (host) {
  initAppBridge({ host, shop });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx; do retry on transient 5xx up to 2x
        if (error?.response?.status >= 400 && error?.response?.status < 500) return false;
        return failureCount < 2;
      },
      staleTime: 30_000,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PolarisProvider i18n={enTranslations}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          {/* App-level: the route boundary lives inside <App />, beside the
              App Bridge nav menu, so it cannot catch a failure in App itself or
              in the providers. Without this one those still blank the app
              entirely (#2752). */}
          <ErrorBoundary title="Brilio Assistant couldn't start" withPage>
            <App />
          </ErrorBoundary>
        </BrowserRouter>
      </QueryClientProvider>
    </PolarisProvider>
  </StrictMode>
);
